import transHelpers from '../../util/transHelpers';
import { sortItemsByTimestamp } from '../../util/helpers';

export const FILTER_GROUP_TAG = 'tag';
export const FILTER_GROUP_RATING = 'rating';
export const FILTER_GROUP_TEAM = 'team';
export const FILTER_GROUP_PLAYER = 'player';

export const FILTER_GROUPS = [
	FILTER_GROUP_TAG,
	FILTER_GROUP_RATING,
	FILTER_GROUP_TEAM,
	FILTER_GROUP_PLAYER,
];

export const initialState = () => {
	return {
		[FILTER_GROUP_TAG]:    [],
		[FILTER_GROUP_RATING]: [],
		[FILTER_GROUP_TEAM]:   [],
		[FILTER_GROUP_PLAYER]: [],
		activeFilters:         [],
		sorting:               {},
		// list of tags (TagButtons) that would be excluded from the filters, regardless of the number of results they might have.
		// Thus, the corresponding event tags won't show up in the filtered list either.
		excludedTagNames:      [],
		sourceTags:            null,
		searchKeyword:         null,
	};
};

const eventTagFilterId = (eventTag) => {
	return `${eventTag.tag.id}-${eventTag.name}`;
};

const countFilterTags = (
	allEventTags,
	groupedActiveFilters,
	{ filterType, filterId },
	event
) => {
	let count = 0;
	const isPlayerCompetition = event.isPlayerCompetition;

	const tagsCountFn = (acc, eventTag) => {
		if (!eventTag.player &&
			groupedActiveFilters[FILTER_GROUP_TEAM].length &&
			!groupedActiveFilters[FILTER_GROUP_TEAM].find(f => f.id === eventTag.team?.id)
		) {
			return acc;
		}

		const allPlayerFilters = groupedActiveFilters[FILTER_GROUP_PLAYER];

		if (isPlayerCompetition &&
			groupedActiveFilters[FILTER_GROUP_PLAYER].length &&
			!groupedActiveFilters[FILTER_GROUP_PLAYER].find(f => f.id === eventTag.player?.id)
		) {
			return acc;
		} else if (!isPlayerCompetition && allPlayerFilters.length) {
			if (!allPlayerFilters.find(f => eventTag.player?.id === f.id)) {
				return acc;
			}
		}

		if (eventTagFilterId(eventTag) === filterId) {
			return acc + 1;
		}

		return acc;
	};

	const ratingCountFn = (acc, eventTag) => {
		if (eventTag.rating === filterId) {
			return acc + 1;
		}

		return acc;
	};

	const teamsCountFn = (acc, eventTag) => {
		if (groupedActiveFilters[FILTER_GROUP_TAG].length &&
			!groupedActiveFilters[FILTER_GROUP_TAG].find(f => f.id === eventTagFilterId(eventTag))
		) {
			return acc;
		}

		if (eventTag.team?.id === filterId) {
			return acc + 1;
		}

		return acc;
	};

	const playerCountFn = (acc, eventTag) => {
		if (groupedActiveFilters[FILTER_GROUP_TAG].length &&
			!groupedActiveFilters[FILTER_GROUP_TAG].find(f => f.id === eventTagFilterId(eventTag))
		) {
			return acc;
		}

		if (eventTag.player?.id === filterId) {
			return acc + 1;
		}

		return acc;
	};

	let countFn;
	switch (filterType) {
	case FILTER_GROUP_TAG:
		countFn = tagsCountFn;
		break;
	case FILTER_GROUP_RATING:
		countFn = ratingCountFn;
		break;
	case FILTER_GROUP_TEAM:
		countFn = teamsCountFn;
		break;
	case FILTER_GROUP_PLAYER:
		countFn = playerCountFn;
		break;
	}

	count = allEventTags.reduce(countFn, 0);

	return count;
};

const buildFilterEntry = (
	id,
	type,
	name,
	label,
	count
) => {
	const keyPrefix = 'clip-filter';
	return {
		id,
		key:   `${keyPrefix}-${type}-${id}`,
		type,
		name,
		label: label,
		count,
	};
};

const remainingTeamFilters = (teams, existingTeamFilters) => {
	const ret = [];
	for (const team of teams) {
		if (!existingTeamFilters.find(f => f.type === FILTER_GROUP_TEAM && f.id === team.id)) {
			ret.push(
				buildFilterEntry(
					team.id,
					FILTER_GROUP_TEAM,
					team.realName,
					team.realName,
					0
				)
			);
		}
	}

	return ret;
};

const remainingPlayerFilters = (team, existingPlayerFilters) => {
	const teamPlayers = team ? team.players : [];
	return [
		...(() => {
			const ret = [];
			for (const player of teamPlayers) {
				if (!existingPlayerFilters.find(clipFilter =>
					clipFilter.type ===	FILTER_GROUP_PLAYER &&
					clipFilter.id === player.id
				)
				) {
					if (teamPlayers.find((_player) => player.id === _player.id)) {
						ret.push(buildFilterEntry(
							player.id,
							FILTER_GROUP_PLAYER,
							player.name,
							player.name,
							0,
						));
					}
				}
			}

			return ret;
		})(),
	];
};

const createTeamsPlayerIdMapping = (teams = []) => {
	const teamPlayerIds = {};
	for (const team of teams) {
		teamPlayerIds[team.id] = createTeamPlayerIdMapping(team);
	}
	return teamPlayerIds;
};

const createTeamPlayerIdMapping = (team) => {
	return team.players?.map((player) => player.id) || [];
};

const getActiveTeamPlayerFilters = (teams, playerFilters) => {
	const teamPlayerIds = createTeamsPlayerIdMapping(teams);
	return teams.reduce(
		(acc, team) => {
			acc[team.id] = playerFilters.filter(
				(f) => teamPlayerIds[team.id].includes(f.id)
			);
			return acc;
		},
		{}
	);
};

const filterTagsByKeyword = (tags, keyword, sport) => {
	let items = tags;
	const keyw = keyword?.trim().toLowerCase();

	if (!keyw?.length) {
		return items;
	}

	const getTagHaystacks = (tag) => {
		const teamCode = tag.team?.club?.code?.toLowerCase();
		const playerName = tag.player?.name.toLowerCase();
		return [
			transHelpers.transTagLabel(tag.name, sport, tag.tag?.type)
				.toLowerCase()
				.replaceAll(/[,.-]/ig, ' '),
			...(teamCode ? [teamCode] : []),
			...(playerName ? [playerName] : []),
		];
	};

	if (keyw?.length === 1) {
		items = items.filter((tag) => {
			const haystacks = getTagHaystacks(tag);
			return haystacks.some(haystack => haystack.startsWith(keyw));
		});
	} else {
		items = items.filter((tag) => {
			const haystacks = getTagHaystacks(tag);
			return haystacks.some(haystack => haystack.includes(keyw));
		});
	}

	return items;
};

export default {
	namespaced:      true,
	state:           initialState,
	runInNewContext: true,
	mutations:       {
		setTagFilters(state, filters) {
			state[FILTER_GROUP_TAG] = filters;
		},
		setTeamFilters(state, filters) {
			state[FILTER_GROUP_TEAM] = filters;
		},
		setPlayerFilters(state, filters) {
			state[FILTER_GROUP_PLAYER] = filters;
		},
		setActiveFilters(state, filters) {
			state.activeFilters = [...filters];
		},
		setSorting(state, { component, key, direction }) {
			state.sorting[component] = {
				key,
				direction,
			};
		},
		setExcludedTags(state, tagNames) {
			state.excludedTagNames = [...tagNames];
		},
		setSourceTags(state, tags) {
			state.sourceTags = tags;
		},
		setSearchKeyword(state, kw) {
			state.searchKeyword = kw;
		},
	},
	actions: {
		init({ dispatch }) {
			dispatch('updateAvailableFilters');
		},
		setSorting({ commit }, value) {
			commit('setSorting', value);
		},
		updateActiveFilters({ commit, dispatch }, filters) {
			commit('setActiveFilters', filters);
			dispatch('updateAvailableFilters');
		},
		updateActiveTeamAndPlayerFilters({ dispatch, getters }, { team, filters } = []) {
			let activeFilters = [...getters.activeFilters];
			const teamPlayerMapping = createTeamPlayerIdMapping(team);

			// remove team & team player active tags
			activeFilters = activeFilters.filter(
				f => {
					return !(
						f.type === FILTER_GROUP_TEAM && f.id === team.id ||
						f.type === FILTER_GROUP_PLAYER && teamPlayerMapping.includes(f.id)
					);
				}
			);

			activeFilters.push(...filters);
			dispatch('updateActiveFilters', activeFilters);
		},
		updateAvailableFilters({ getters, commit }) {
			const availableFilters = getters.groupedFilters;
			commit('setTagFilters', availableFilters[FILTER_GROUP_TAG]);
			commit('setTeamFilters', availableFilters[FILTER_GROUP_TEAM]);
			commit('setPlayerFilters', availableFilters[FILTER_GROUP_PLAYER]);
		},
		setExcludedTags({ commit }, tagNames) {
			commit('setExcludedTags', tagNames);
		},
		setSourceTags({ commit }, tags) {
			commit('setSourceTags', tags);
		},
		searchClips({ commit }, keyword) {
			commit('setSearchKeyword', keyword);
		},
		clearSearch({ commit }) {
			commit('setSearchKeyword', null);
		},
	},
	getters: {
		sorting:            (state) => state.sorting,
		activeFilters:      (state) => state.activeFilters,
		availableEventTags: (state, getters, rootState, rootGetters) => {
			return state.sourceTags
				? state.sourceTags
				: rootGetters['event/tags'].filter(eventTag => !state.excludedTagNames.some(name => name === eventTag.tag.name));
		},
		groupedFilters: (state, getters, rootState, rootGetters) => {
			const event = rootGetters['event/event'];
			const allEventTags = getters.availableEventTags;
			const filteredByKeyword = filterTagsByKeyword(allEventTags, getters.searchKeyword, rootGetters['event/sportName']);
			const sportName = rootGetters['event/sportName'];
			const activeFilters = getters.groupedActiveFilters;
			const teamFilters = [];
			const tagFilters = [];
			const ratingFilters = [];
			const playerFilters = [];

			for (const eventTag of allEventTags) {
				// add tag types
				if (!tagFilters.find((filter) => filter.type === FILTER_GROUP_TAG && filter.name === eventTag.name)) {
					const filterId = eventTagFilterId(eventTag) || 0;
					const filter = buildFilterEntry(
						filterId,
						FILTER_GROUP_TAG,
						eventTag.name,
						transHelpers.transTagLabel(eventTag.name, sportName, eventTag.tag?.type),
						countFilterTags(
							filteredByKeyword,
							activeFilters,
							{ filterType: FILTER_GROUP_TAG, filterId },
							event
						),
					);
					tagFilters.push(filter);
				}

				// add rating types
				if (eventTag.rating && !ratingFilters.find((filter) => filter.type === FILTER_GROUP_RATING && filter.name === eventTag.rating)) {
					const filterId = eventTag.rating || 0;
					const filter = buildFilterEntry(
						filterId,
						FILTER_GROUP_RATING,
						eventTag.rating,
						eventTag.rating,
						countFilterTags(
							filteredByKeyword,
							activeFilters,
							{ filterType: FILTER_GROUP_RATING, filterId },
							event
						)
					);
					ratingFilters.push(filter);
				}

				// add teams
				if (eventTag.team && !teamFilters.find((filter) => filter.type === FILTER_GROUP_TEAM && filter.id === eventTag.team.id)) {
					const filterId = eventTag.team.id;
					const filter = buildFilterEntry(
						filterId,
						FILTER_GROUP_TEAM,
						eventTag.team.club?.code ?? eventTag.team.name,
						eventTag.team.club?.code ?? eventTag.team.name,
						countFilterTags(
							filteredByKeyword,
							activeFilters,
							{ filterType: FILTER_GROUP_TEAM, filterId },
							event
						)
					);
					teamFilters.push(filter);
				}

				// add players
				if (eventTag.player && !playerFilters.find((filter) => filter.type === FILTER_GROUP_PLAYER && filter.id === eventTag.player.id)) {
					const filterId = eventTag.player.id;
					const filter = buildFilterEntry(
						filterId,
						FILTER_GROUP_PLAYER,
						eventTag.player.name,
						eventTag.player.name,
						countFilterTags(
							filteredByKeyword,
							activeFilters,
							{ filterType: FILTER_GROUP_PLAYER, filterId },
							event
						)
					);
					playerFilters.push(filter);
				}
			}

			// add "remaining filters" for teams and player.
			// These are filters that couldn't be extracted from existing event tags,
			// and will always have 0 usage count
			teamFilters.push(...remainingTeamFilters(event.teams, teamFilters));

			for (const team of event.teams) {
				playerFilters.push(...remainingPlayerFilters(team, playerFilters));
			}

			return {
				[FILTER_GROUP_TAG]:    tagFilters,
				[FILTER_GROUP_RATING]: ratingFilters,
				[FILTER_GROUP_TEAM]:   teamFilters,
				[FILTER_GROUP_PLAYER]: playerFilters,
			};
		},
		groupedActiveFilters(state, getters) {
			return {
				[FILTER_GROUP_TAG]:    [...getters.activeTagFilters],
				[FILTER_GROUP_RATING]: [...getters.activeRatingFilters],
				[FILTER_GROUP_TEAM]:   [...getters.allActiveTeamFilters],
				[FILTER_GROUP_PLAYER]: [...getters.allActivePlayerFilters],
			};
		},
		activeTagFilters(state, getters) {
			return getters.activeFilters.filter(
				f => f.type === FILTER_GROUP_TAG
			) || [];
		},
		activeRatingFilters(state, getters) {
			return getters.activeFilters.filter(
				f => f.type === FILTER_GROUP_RATING
			) || [];
		},
		allActiveTeamFilters: (state, getters) => {
			return getters.activeFilters.filter(f => f.type === FILTER_GROUP_TEAM);
		},
		teamAndPlayerFilters: (state, getters, rootState, rootGetters) => (team) => {
			const teamEntity = !isNaN(team) ? rootGetters['event/event'].teamById(team) : team;
			const teamPlayerMapping = createTeamPlayerIdMapping(teamEntity);

			return [
				...state[FILTER_GROUP_TEAM].filter(f => f.id === team.id),
				...getters.allPlayerFilters.filter(f => teamPlayerMapping.includes(f.id)),
			];
		},
		activeTeamAndPlayerFilters: (state, getters, rootState, rootGetters) => (team) => {
			const teamEntity = !isNaN(team) ? rootGetters['event/event'].teamById(team) : team;
			const teamPlayerMapping = createTeamPlayerIdMapping(teamEntity);

			return [
				...getters.allActiveTeamFilters.filter(f => f.id === teamEntity.id),
				...getters.allActivePlayerFilters.filter(f => teamPlayerMapping.includes(f.id)),
			];
		},
		allActivePlayerFilters(state, getters) {
			return getters.activeFilters.filter(clipFilter => clipFilter.type === FILTER_GROUP_PLAYER);
		},
		allPlayerFilters(state) {
			return state[FILTER_GROUP_PLAYER];
		},
		filteredClips(state, getters, rootState, rootGetters) {
			const event = rootGetters['event/event'];
			const groupedActiveFilters = getters.groupedActiveFilters;
			const eventTags = getters.availableEventTags;
			const isPlayerCompetition = rootGetters['event/event'].isPlayerCompetition;

			// create a map with { teamId => [playerIds] }
			const activeTeamPlayerFilters = getActiveTeamPlayerFilters(event.teams || [], groupedActiveFilters[FILTER_GROUP_PLAYER]);
			let items = eventTags.filter((eventTag) => {
				if (!getters.activeFilters.length) {
					// no filters set
					return true;
				}

				if (groupedActiveFilters[FILTER_GROUP_TAG].length && !groupedActiveFilters[FILTER_GROUP_TAG].find(f => f.id === eventTagFilterId(eventTag))) {
					return false;
				}

				if (groupedActiveFilters[FILTER_GROUP_RATING].length && !groupedActiveFilters[FILTER_GROUP_RATING].find(f => {
					return f.id === eventTag.rating;
				})) {
					return false;
				}

				// single sport conditions
				if (isPlayerCompetition) {
					return !(groupedActiveFilters[FILTER_GROUP_PLAYER].length && !groupedActiveFilters[FILTER_GROUP_PLAYER].find(f => f.id === eventTag.player?.id));
				}

				// team sport conditions below
				// get all active player filters for event tag's team
				const currentTeamActivePlayerFilters = activeTeamPlayerFilters[eventTag.team?.id] || [];

				// check team conditions
				if (groupedActiveFilters[FILTER_GROUP_TEAM].length) {
					// if the team has no active player filters then check if the team filters match
					if (!currentTeamActivePlayerFilters.length) {
						// check if the event team matches filters
						return groupedActiveFilters[FILTER_GROUP_TEAM].find(f => f.id === eventTag.team?.id);
					}
				}

				// check player conditions
				if (groupedActiveFilters[FILTER_GROUP_PLAYER].length) {
					if (!eventTag.team) {
						return false;
					}

					if (currentTeamActivePlayerFilters.length) {
						if (currentTeamActivePlayerFilters.find(f => f.id === eventTag.player?.id)) {
							return true;
						}
					}
					// player filters don't match
					return false;
				}
				return true;
			});

			// filter by keyword
			if (getters.searchKeyword?.trim().length) {
				items = filterTagsByKeyword(items, getters.searchKeyword, rootGetters['event/sportName']);
			}
			// show filtered clips list with the latest clips first
			return sortItemsByTimestamp(
				items
			);
		},
		searchKeyword: (state) => state.searchKeyword,
		isFiltered:    (getters) => getters.activeFilters.length > 0,
	},
};
